/**
 * AADHYAA — Email authentication page (reverted from mobile OTP, iter 283).
 *
 * Single page, two tabs:
 *   • Sign in  → POST /api/auth/login    {email, password}
 *   • Sign up  → POST /api/auth/register {email, password, name, accepted_terms}
 *
 * Mobile-OTP backend (/api/auth/mobile/*) is intentionally left intact so the
 * project can flip back once MSG91 SMS delivery stabilises.
 *
 * Post-auth redirect rules live in routes-config.jsx (the `/auth` guard).
 * Here we just call loginWithToken() and let the guard route to /dashboard or
 * /onboarding depending on `has_birth_chart`.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  ArrowRight,
  ShieldCheck,
  EnvelopeSimple,
  Lock,
  User as UserIcon,
  Sparkle,
} from "@phosphor-icons/react";

import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Logo } from "../components/Logo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();

  // 'signin' | 'signup'  — query-param `?mode=register` opens signup tab
  const [mode, setMode] = useState(
    searchParams.get("mode") === "register" ? "signup" : "signin"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [loading, setLoading] = useState(false);

  // Sync `?mode=` on URL change (e.g. coming from /signup alias)
  useEffect(() => {
    const m = searchParams.get("mode");
    if (m === "register" && mode !== "signup") setMode("signup");
  }, [searchParams, mode]);

  const switchMode = (m) => {
    setMode(m);
    // Soft-clear sensitive field on tab switch
    setPassword("");
  };

  const validate = () => {
    const e = email.trim().toLowerCase();
    if (!EMAIL_RX.test(e)) {
      toast.error("Please enter a valid email address.");
      return null;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return null;
    }
    if (mode === "signup") {
      if (name.trim().length < 2) {
        toast.error("Please enter your name (at least 2 characters).");
        return null;
      }
      if (!acceptedTerms) {
        toast.error("Please accept the Terms and Privacy Policy.");
        return null;
      }
    }
    return { email: e, password, name: name.trim() };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const payload = validate();
    if (!payload) return;

    setLoading(true);
    try {
      const endpoint =
        mode === "signin" ? "/api/auth/login" : "/api/auth/register";
      const body =
        mode === "signin"
          ? { email: payload.email, password: payload.password }
          : {
              email: payload.email,
              password: payload.password,
              name: payload.name,
              accepted_terms: true,
            };

      const resp = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data?.detail || data?.message || "Authentication failed.";
        throw new Error(detail);
      }

      const { access_token, user } = data;
      if (!access_token || !user) {
        throw new Error("Unexpected response from server.");
      }

      loginWithToken(access_token, user);

      toast.success(
        mode === "signin"
          ? `Welcome back, ${user.name?.split(" ")[0] || "friend"}.`
          : `Welcome to AADHYAA, ${user.name?.split(" ")[0] || "friend"}.`
      );

      // Let the /auth route guard handle the redirect on next render —
      // but for a direct navigate(), we also push to dashboard/onboarding.
      navigate(user?.has_birth_chart ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8 bg-[var(--bg-base)]"
      data-testid="auth-page"
    >
      <Helmet>
        <title>{mode === "signin" ? "Sign in" : "Create account"} — AADHYAA</title>
        <meta name="description" content="Sign in or create your AADHYAA account to access personalised Vedic guidance, daily snapshots, and reports." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center gap-3 mb-7">
          <Logo size={48} />
          <h1 className="text-2xl font-medium text-[var(--text-primary)] text-center">
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-[var(--text-muted)] text-center max-w-sm">
            {mode === "signin"
              ? "Sign in to continue your Vedic journey."
              : "Begin your personalised Vedic guidance — daily snapshots, reports, and timing intelligence."}
          </p>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          className="grid grid-cols-2 gap-1 p-1 mb-5 rounded-xl bg-[var(--bg-card)] border border-[var(--border-muted)]"
          data-testid="auth-tabs"
        >
          <button
            role="tab"
            type="button"
            aria-selected={mode === "signin"}
            onClick={() => switchMode("signin")}
            className={`py-2.5 text-sm font-medium rounded-lg transition-colors ${
              mode === "signin"
                ? "bg-[var(--truffle)] text-white"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
            data-testid="signin-tab-btn"
          >
            Sign in
          </button>
          <button
            role="tab"
            type="button"
            aria-selected={mode === "signup"}
            onClick={() => switchMode("signup")}
            className={`py-2.5 text-sm font-medium rounded-lg transition-colors ${
              mode === "signup"
                ? "bg-[var(--truffle)] text-white"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
            data-testid="signup-tab-btn"
          >
            Create account
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[var(--bg-card)] border border-[var(--border-gold)]/30 rounded-2xl p-6 sm:p-8 space-y-5 shadow-sm"
          data-testid="auth-form"
        >
          <AnimatePresence mode="wait">
            {mode === "signup" && (
              <motion.div
                key="name-field"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                className="space-y-2 overflow-hidden"
              >
                <label
                  htmlFor="auth-name"
                  className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-1.5"
                >
                  <UserIcon size={14} weight="duotone" /> Full name
                </label>
                <Input
                  id="auth-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 text-base rounded-xl"
                  data-testid="auth-name-input"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-2">
            <label
              htmlFor="auth-email"
              className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-1.5"
            >
              <EnvelopeSimple size={14} weight="duotone" /> Email
            </label>
            <Input
              id="auth-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 text-base rounded-xl"
              data-testid="auth-email-input"
              autoFocus={mode === "signin"}
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="auth-password"
              className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-1.5"
            >
              <Lock size={14} weight="duotone" /> Password
            </label>
            <Input
              id="auth-password"
              type="password"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 text-base rounded-xl"
              data-testid="auth-password-input"
            />
            {mode === "signin" && (
              <div className="flex justify-end pt-1">
                <a
                  href="/reset-password"
                  className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] underline-offset-2 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </a>
              </div>
            )}
          </div>

          {mode === "signup" && (
            <label className="flex items-start gap-2 text-xs text-[var(--text-muted)] cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 accent-[var(--truffle)]"
                data-testid="auth-terms-checkbox"
              />
              <span>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--text-primary)]">
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--text-primary)]">
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          )}

          <Button
            type="submit"
            disabled={loading || !email.trim() || !password || (mode === "signup" && !name.trim())}
            className="w-full h-12 rounded-xl bg-[var(--truffle)] hover:bg-[var(--truffle)]/90 text-white text-base font-medium disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                {mode === "signin" ? "Signing in..." : "Creating account..."}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                {mode === "signin" ? (
                  <>
                    <ShieldCheck size={18} weight="fill" /> Sign in
                  </>
                ) : (
                  <>
                    <Sparkle size={18} weight="fill" /> Create account
                  </>
                )}
                <ArrowRight size={18} />
              </span>
            )}
          </Button>

          <p className="text-xs text-center text-[var(--text-muted)] pt-2 border-t border-[var(--border-muted)]">
            {mode === "signin" ? (
              <>
                New to AADHYAA?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("signup")}
                  className="underline hover:text-[var(--text-primary)]"
                  data-testid="switch-to-signup-link"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="underline hover:text-[var(--text-primary)]"
                  data-testid="switch-to-signin-link"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default AuthPage;
